import './MapContainer.scss';

import { mdiCrosshairsGps } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useState } from 'react';

import { CustomMarker } from './CustomMarker';
import GoogleMapsApiBoundary from './GoogleMapsApiBoundary';
import GoogleMapsHtmlMarker from './GoogleMapsHtmlMarker';
import GoogleMapsMap from './GoogleMapsMap';
import type { MarkerIconType } from './MarkerIcon';

export const DEFAULT_COORDINATES = { lat: 49.817492, lng: 15.472962 };
const DEFAULT_ZOOM = 12;
export const MARKER_ZOOM = 15;

export interface LocationMarker {
  id: string | number;
  latitude: number;
  longitude: number;
  popup?: React.ReactNode;
  content?: React.ReactNode;
  type: MarkerIconType;
}

interface Props {
  markers?: LocationMarker[];
  selectAddress?: (id: string) => void;
  latitude?: number;
  longitude?: number;
  onMapLoad?: (map: google.maps.Map, maps: typeof google.maps) => void;
  onMapDrag?: (map: google.maps.Map) => void;
  onDragEnd?: (lat: number, lng: number) => void;
  onDrag?: (lat: number, lng: number) => void;
  selectedAddressId?: string;
  clickableMarker: boolean;
  resetMarkerPosition?: () => void;
  markerMoved?: boolean;
  showCurrentLocationButton?: boolean;
  onCurrentLocationClick?: () => void;
  onClick?: (value: google.maps.LatLngLiteral) => void;
}

const MapContainer: React.FC<Props> = (props): React.ReactElement => {
  const {
    markers,
    selectAddress,
    selectedAddressId,
    clickableMarker,
    onDrag,
    onDragEnd,
    latitude,
    longitude,
    markerMoved,
    onMapDrag,
    onMapLoad,
    resetMarkerPosition,
    onCurrentLocationClick,
    showCurrentLocationButton,
    onClick
  } = props;

  const { t } = useCustomTranslation();

  const [isMarkerDragging, setIsMarkerDragging] = useState(false);

  const hasCustomCenter = !markers && latitude !== undefined && longitude !== undefined;

  // Freeze the controlled center while the marker is dragged so the map does not fight the cursor.
  const markerCenter = hasCustomCenter ? { lat: latitude, lng: longitude } : undefined;
  const center = isMarkerDragging ? undefined : markerCenter;

  const renderMarks = () => {
    if (!markers) return null;

    return markers.map((marker) => {
      return (
        <GoogleMapsHtmlMarker
          key={marker.id}
          latitude={marker.latitude}
          longitude={marker.longitude}
          selected={selectedAddressId === String(marker.id)}
        >
          <CustomMarker
            lat={marker.latitude}
            lng={marker.longitude}
            onClick={() => selectAddress?.(String(marker.id))}
            content={marker.content}
            popup={marker.popup}
            selected={selectedAddressId === String(marker.id)}
            clickableMarker={clickableMarker}
            type={marker.type}
          />
        </GoogleMapsHtmlMarker>
      );
    });
  };

  return (
    // Important! Always set the container height explicitly
    <div className="MapContainer">
      {markerMoved && (
        <Button className="MapContainer_reset" onClick={resetMarkerPosition}>
          {t('mapContainer.resetButton')}
        </Button>
      )}
      {showCurrentLocationButton && (
        <Button
          className="MapContainer_currentPosition"
          onClick={() => onCurrentLocationClick?.()}
          icon={<Icon path={mdiCrosshairsGps} size={1} />}
        />
      )}
      <GoogleMapsApiBoundary>
        <GoogleMapsMap
          defaultCenter={markerCenter ?? DEFAULT_COORDINATES}
          center={center}
          zoom={hasCustomCenter ? MARKER_ZOOM : DEFAULT_ZOOM}
          onDrag={(event) => onMapDrag?.(event.map)}
          onMapLoad={onMapLoad}
          onClick={(event) => {
            if (event.detail.latLng) onClick?.(event.detail.latLng);
          }}
        >
          {!markers
            ? markerCenter && (
                <GoogleMapsHtmlMarker
                  latitude={markerCenter.lat}
                  longitude={markerCenter.lng}
                  draggable={!!(onDrag || onDragEnd)}
                  onDragStart={() => setIsMarkerDragging(true)}
                  onDrag={onDrag}
                  onDragEnd={(lat, lng) => {
                    onDragEnd?.(lat, lng);
                    setIsMarkerDragging(false);
                  }}
                  onDragCancel={() => setIsMarkerDragging(false)}
                >
                  <CustomMarker
                    lat={markerCenter.lat}
                    lng={markerCenter.lng}
                    clickableMarker={clickableMarker}
                    type="single"
                  />
                </GoogleMapsHtmlMarker>
              )
            : renderMarks()}
        </GoogleMapsMap>
      </GoogleMapsApiBoundary>
    </div>
  );
};

export default MapContainer;
